import { useEffect, useState } from 'react';
import { Outlet, useLocation, Link } from 'react-router-dom';
import { Send, Loader2, ChevronLeft } from 'lucide-react';

import { SidebarProvider, SidebarInset, SidebarTrigger } from '../components/ui/sidebar';
import { AppSidebar } from '../widgets/app-sidebar';
import { MobileTabBar } from '../widgets/mobile-tab-bar';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '../components/ui/breadcrumb';
import { Button } from '../components/ui/button';
import { ROUTES, NAV_SECTIONS, isSectionActive, rememberSection } from '../shared/config/routes';
import { TopbarSlotProvider } from '../shared/ui/TopbarSlot';
import { usePublishStore } from '../widgets/post-create-form';
import { useReelPublishStore } from '../widgets/reel-create-form';
import { usePublicationFormat } from '../pages/publications/PublicationNewPage';

/** Экраны-редакторы: у них своя нижняя панель действий, таб-бар там мешает. */
function isFullscreenEditor(path: string): boolean {
  return path === ROUTES.PUBLICATION_NEW || path.startsWith('/templates/');
}

/**
 * Хлебные крошки нужны только на подэкранах: у разделов в топбаре стоит имя
 * раздела, и подменять его цепочкой из одного звена незачем.
 */
function useBreadcrumb(): { parent: { name: string; path: string }; current: string } | null {
  const { pathname } = useLocation();

  if (pathname === ROUTES.PUBLICATION_NEW) {
    return { parent: { name: 'Публикации', path: ROUTES.PUBLICATIONS }, current: 'Новая публикация' };
  }
  if (pathname === ROUTES.STATS_REPORT) {
    return { parent: { name: 'Статистика', path: ROUTES.STATS }, current: 'Выгрузки' };
  }
  if (pathname === '/stats/history') {
    return { parent: { name: 'Статистика', path: ROUTES.STATS }, current: 'История сборов' };
  }
  if (pathname === ROUTES.TEMPLATE_NEW) {
    return { parent: { name: 'Аккаунты', path: `${ROUTES.ACCOUNTS}/templates` }, current: 'Новый шаблон' };
  }
  if (pathname.startsWith('/templates/') && pathname.endsWith('/edit')) {
    return { parent: { name: 'Аккаунты', path: `${ROUTES.ACCOUNTS}/templates` }, current: 'Шаблон оформления' };
  }
  // Вкладки раздела — это сам раздел, а не подэкран «Публикация».
  const publicationTabs = [`${ROUTES.PUBLICATIONS}/feed`, `${ROUTES.PUBLICATIONS}/scheduled`, ROUTES.PUBLICATIONS_COMMENTS];
  if (pathname.startsWith('/publications/') && !publicationTabs.includes(pathname)) {
    return { parent: { name: 'Публикации', path: ROUTES.PUBLICATIONS }, current: 'Публикация' };
  }
  if (pathname.startsWith('/all-publications/')) {
    return { parent: { name: 'Публикации', path: `${ROUTES.PUBLICATIONS}/feed` }, current: 'Публикация' };
  }
  if (pathname.startsWith('/analytics/account/')) {
    return { parent: { name: 'Статистика', path: ROUTES.STATS_ACCOUNTS }, current: 'Аккаунт' };
  }
  return null;
}

/** Кнопка «Опубликовать» для экрана создания — форма отдаёт свой обработчик через стор. */
function PublishAction(): JSX.Element | null {
  const format = usePublicationFormat();
  const post = usePublishStore();
  const reel = useReelPublishStore();
  const { disabled, loading, handlePublish, label, hint } = format === 'reel' ? reel : post;

  if (!handlePublish) return null;

  // Не гаснет молча: при нехватке чего-то нажатие покажет, чего именно.
  return (
    <Button onClick={handlePublish} disabled={disabled} className="hidden lg:flex" title={hint || undefined}>
      {loading ? (
        <>
          <Loader2 className="h-4 w-4 animate-spin" />
          Публикуем…
        </>
      ) : (
        <>
          <Send className="h-4 w-4" />
          {label}
        </>
      )}
    </Button>
  );
}

export function Layout() {
  const { pathname } = useLocation();
  const breadcrumb = useBreadcrumb();
  // Заголовок «Монтажа» — в шапке сайта, как у остальных разделов: крупный
  // заголовок на странице съедал место над доской, особенно на телефоне (08.10.2026).
  const montageTitle = pathname.startsWith(ROUTES.MODERATION) ? 'Модерация роликов'
    : pathname.startsWith(ROUTES.MY_TASKS) ? 'Мои задачи'
    : pathname.startsWith(ROUTES.VIDEOS) ? 'Видео'
    : pathname.startsWith(ROUTES.TAKES) ? 'Ролики B2G'
    : pathname.startsWith(ROUTES.WORK) ? 'Отработано'
    : pathname.startsWith(ROUTES.MONTAGE) ? 'Монтаж'
    : null;
  const sectionTitle = montageTitle ?? NAV_SECTIONS.find((section) =>
    isSectionActive(pathname, section.match, (section as { end?: boolean }).end),
  )?.label
    ?? (pathname.startsWith(ROUTES.TEAM) ? 'Сотрудники и доступы' : null);
  const [actionsSlot, setActionsSlot] = useState<HTMLElement | null>(null);
  const fullscreenEditor = isFullscreenEditor(pathname);

  // Последний открытый раздел — с него начнётся следующий заход на «/».
  useEffect(() => { rememberSection(pathname); }, [pathname]);

  return (
    <SidebarProvider>
      <AppSidebar />
      <SidebarInset>
        <TopbarSlotProvider value={actionsSlot}>
          <header className="sticky top-0 z-20 flex h-14 shrink-0 items-center justify-between gap-2 border-b bg-background/95 px-4 backdrop-blur">
            <div className="flex min-w-0 items-center gap-2">
              <SidebarTrigger className="-ml-1" />
              {sectionTitle && !breadcrumb && (
                <h1 className="truncate text-sm font-medium">{sectionTitle}</h1>
              )}
              {/* На телефоне крошки сжаты до текущего экрана, а у редакторов нет
                  таб-бара — без стрелки «назад» из них не выйти. */}
              {breadcrumb && (
                <Link
                  to={breadcrumb.parent.path}
                  aria-label={`Назад: ${breadcrumb.parent.name}`}
                  className="-ml-1 inline-flex shrink-0 items-center text-sm text-muted-foreground hover:text-foreground sm:hidden"
                >
                  <ChevronLeft className="size-5" />
                  <span className="max-w-[7rem] truncate">{breadcrumb.parent.name}</span>
                </Link>
              )}
              {breadcrumb && (
                <Breadcrumb>
                  <BreadcrumbList>
                    <BreadcrumbItem className="hidden sm:block">
                      <BreadcrumbLink asChild>
                        <Link to={breadcrumb.parent.path}>{breadcrumb.parent.name}</Link>
                      </BreadcrumbLink>
                    </BreadcrumbItem>
                    <BreadcrumbSeparator className="hidden sm:block" />
                    <BreadcrumbItem className="hidden sm:inline-flex">
                      <BreadcrumbPage>{breadcrumb.current}</BreadcrumbPage>
                    </BreadcrumbItem>
                  </BreadcrumbList>
                </Breadcrumb>
              )}
            </div>

            <div className="flex shrink-0 items-center gap-2">
              {/* сюда раздел портирует своё действие через <TopbarActions> */}
              <div ref={setActionsSlot} className="flex items-center gap-2" />

              {pathname === ROUTES.PUBLICATION_NEW && <PublishAction />}
            </div>
          </header>

          {/* key по разделу: содержимое проявляется при переходе, но не мигает
              при смене вкладки внутри раздела */}
          <div
            key={pathname.split('/')[1] ?? ''}
            className={`flex min-w-0 flex-1 flex-col gap-4 p-4 duration-200 animate-in fade-in-0 slide-in-from-bottom-1 md:p-6 ${
              fullscreenEditor ? '' : 'pb-24 md:pb-6'
            }`}
          >
            <Outlet />
          </div>
        </TopbarSlotProvider>
      </SidebarInset>

      {!fullscreenEditor && <MobileTabBar />}
    </SidebarProvider>
  );
}
